import { c as _c } from "react/compiler-runtime";
import { useOs } from '@mantine/hooks';
import { Fragment, useEffect, useState } from 'react';
import { isNonNullish } from 'remeda';
import { match } from 'ts-pattern';
import { useLatest } from './hooks';
import { twMerge } from 'tailwind-merge';
import { StyleProps } from 'shared/ui';
export type KbdCombo = {
  mods?: ModifierKeys[];
  key?: KeyboardEventKey;
};
const keyLabel: Partial<Record<KeyboardEventKey, string>> = {
  Meta: '⌘',
  Control: 'ctrl',
  Enter: 'enter',
  Alt: 'alt',
  Escape: 'esc',
  [' ']: 'space'
};
const getKeyLabel = (key: KeyboardEventKey) => keyLabel[key] ?? key;
export const useKbd = () => {
  const $ = _c(10);
  const os = useOs();
  let t0;
  if ($[0] !== os) {
    t0 = m => match(m as string).with("Meta", _ => os == "macos" ? "Meta" : "Control").otherwise(() => m) as T;
    $[0] = os;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const mapKey = t0;
  let t1;
  if ($[2] !== mapKey) {
    t1 = t2 => {
      const {
        mods,
        key
      } = t2;
      return {
        mods: (mods ?? []).map(mapKey),
        key: key ? mapKey(key) : undefined
      };
    };
    $[2] = mapKey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const mapCombo = t1;
  let t2;
  if ($[4] !== mapCombo) {
    t2 = (e, combo) => {
      const {
        key: key_0,
        mods: mods_0
      } = mapCombo(combo);
      return mods_0.every(m_0 => e.getModifierState(m_0)) && (!key_0 || "key" in e && e.key == key_0);
    };
    $[4] = mapCombo;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const kbdMatch = t2;
  let t3;
  if ($[6] !== kbdMatch || $[7] !== mapCombo || $[8] !== mapKey) {
    t3 = {
      kbdMatch,
      mapCombo,
      mapKey
    };
    $[6] = kbdMatch;
    $[7] = mapCombo;
    $[8] = mapKey;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
export const KbdComboLabel = t0 => {
  const $ = _c(8);
  const {
    combo,
    className,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "badge" : t1;
  const {
    mapCombo
  } = useKbd();
  let t2;
  if ($[0] !== className || $[1] !== combo || $[2] !== mapCombo || $[3] !== variant) {
    const osCombo = mapCombo(combo);
    const t3 = variant == "badge" && "text-gray-500 bg-gray-500/10 rounded-xl py-1 px-3";
    let t4;
    if ($[5] !== className || $[6] !== t3) {
      t4 = twMerge("inline-flex items-center gap-1 text-sm font-bold ", t3, className);
      $[5] = className;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    t2 = <div className={t4}>{[...(osCombo.mods ?? []), osCombo.key].filter(isNonNullish).map(getKeyLabel).map(_temp)}</div>;
    $[0] = className;
    $[1] = combo;
    $[2] = mapCombo;
    $[3] = variant;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const KbdComboTip = t0 => {
  const $ = _c(7);
  const {
    title,
    combo
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <div className="font-semibold">{title}</div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== combo) {
    t2 = combo && <div className="mt-2"><KbdComboLabel combo={combo} /></div>;
    $[2] = combo;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="m-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};

// prettier-ignore
type NumericKeypadKeys = "Decimal" | "Key11" | "Key12" | "Multiply" | "Add" | "Clear" | "Divide" | "Subtract" | "Separator" | "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9";
// prettier-ignore
type UpperAlpha = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K' | 'L' | 'M' | 'N' | 'O' | 'P' | 'Q' | 'R' | 'S' | 'T' | 'U' | 'V' | 'W' | 'X' | 'Y' | 'Z';
// prettier-ignore
type LowerAlpha = 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h' | 'i' | 'j' | 'k' | 'l' | 'm' | 'n' | 'o' | 'p' | 'q' | 'r' | 's' | 't' | 'u' | 'v' | 'w' | 'x' | 'y' | 'z';
// prettier-ignore
type ModifierKeys = "Alt" | "AltGraph" | "CapsLock" | "Control" | "Fn" | "FnLock" | "Hyper" | "Meta" | "NumLock" | "ScrollLock" | "Shift" | "Super" | "Symbol" | "SymbolLock";
type WhitespaceKeys = 'Enter' | 'Tab' | ' ';
// prettier-ignore
type NavigationKeys = "ArrowDown" | "ArrowLeft" | "ArrowRight" | "ArrowUp" | "End" | "Home" | "PageDown" | "PageUp";
// prettier-ignore
type EditingKeys = "Backspace" | "Clear" | "Copy" | "CrSel" | "Cut" | "Delete" | "EraseEof" | "ExSel" | "Insert" | "Paste" | "Redo" | "Undo";
// prettier-ignore
type UIKeys = "Accept" | "Again" | "Attn" | "Cancel" | "ContextMenu" | "Escape" | "Execute" | "Find" | "Finish" | "Help" | "Pause" | "Play" | "Props" | "Select" | "ZoomIn" | "ZoomOut";
// prettier-ignore
type DeviceKeys = "BrightnessDown" | "BrightnessUp" | "Eject" | "LogOff" | "Power" | "PowerOff" | "PrintScreen" | "Hibernate" | "Standby" | "WakeUp";
// prettier-ignore
type IMECompositionKeys = "AllCandidates" | "Alphanumeric" | "CodeInput" | "Compose" | "Convert" | "Dead" | "FinalMode" | "GroupFirst" | "GroupLast" | "GroupNext" | "GroupPrevious" | "ModeChange" | "NextCandidate" | "NonConvert" | "PreviousCandidate" | "Process" | "SingleCandidate";
// prettier-ignore
type FunctionKeys = "F1" | "F2" | "F3" | "F4" | "F5" | "F6" | "F7" | "F8" | "F9" | "F10" | "F11" | "F12" | "F13" | "F14" | "F15" | "F16" | "F17" | "F18" | "F19" | "F20" | "Soft1" | "Soft2" | "Soft3" | "Soft4";
// prettier-ignore
type PhoneKeys = "AppSwitch" | "Call" | "Camera" | "CameraFocus" | "EndCall" | "GoBack" | "GoHome" | "HeadsetHook" | "LastNumberRedial" | "Notification" | "MannerMode" | "VoiceDial";
// prettier-ignore
type MultimediaKeys = "ChannelDown" | "ChannelUp" | "MediaFastForward" | "MediaPause" | "MediaPlay" | "MediaPlayPause" | "MediaRecord" | "MediaRewind" | "MediaStop" | "MediaTrackNext" | "MediaTrackPrevious";
// prettier-ignore
type DocumentKeys = "Close" | "New" | "Open" | "Print" | "Save" | "SpellCheck" | "MailForward" | "MailReply" | "MailSend";
// prettier-ignore
type ApplicationSelectorKeys = "LaunchCalculator" | "LaunchCalendar" | "LaunchContacts" | "LaunchMail" | "LaunchMediaPlayer" | "LaunchMusicPlayer" | "LaunchMyComputer" | "LaunchPhone" | "LaunchScreenSaver" | "LaunchSpreadsheet" | "LaunchWebBrowser" | "LaunchWebCam" | "LaunchWordProcessor" | "LaunchApplication1" | "LaunchApplication2" | "LaunchApplication3" | "LaunchApplication4" | "LaunchApplication5" | "LaunchApplication6" | "LaunchApplication7" | "LaunchApplication8" | "LaunchApplication9" | "LaunchApplication10" | "LaunchApplication11" | "LaunchApplication12" | "LaunchApplication13" | "LaunchApplication14" | "LaunchApplication15" | "LaunchApplication16";
export declare type KeyboardEventKey = ModifierKeys | WhitespaceKeys | NavigationKeys | EditingKeys | UIKeys | DeviceKeys | IMECompositionKeys | FunctionKeys | PhoneKeys | MultimediaKeys | DocumentKeys | ApplicationSelectorKeys | NumericKeypadKeys | UpperAlpha | LowerAlpha;
type KeyEventListener = (this: Window, ev: KeyboardEvent) => any;
export const useKeyPress = targetKey => {
  const $ = _c(11);
  const {
    mapKey
  } = useKbd();
  let t0;
  if ($[0] !== mapKey || $[1] !== targetKey) {
    t0 = mapKey(targetKey);
    $[0] = mapKey;
    $[1] = targetKey;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const osTargetKey = useLatest(t0);
  const [keyPressed, setKeyPressed] = useState(false);
  let t1;
  if ($[3] !== osTargetKey) {
    t1 = t2 => {
      const {
        key
      } = t2;
      if (key === osTargetKey.current) {
        setKeyPressed(true);
      }
    };
    $[3] = osTargetKey;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const downHandler = t1;
  let t2;
  if ($[5] !== osTargetKey) {
    t2 = t3 => {
      const {
        key: key_0
      } = t3;
      if (key_0 === osTargetKey.current) {
        setKeyPressed(false);
      }
    };
    $[5] = osTargetKey;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const upHandler = t2;
  let t3;
  if ($[7] !== downHandler || $[8] !== upHandler) {
    t3 = () => {
      window.addEventListener("keydown", downHandler);
      window.addEventListener("keyup", upHandler);
      return () => {
        window.removeEventListener("keydown", downHandler);
        window.removeEventListener("keyup", upHandler);
      };
    };
    $[7] = downHandler;
    $[8] = upHandler;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t3, t4);
  return keyPressed;
};
function _temp(m, i) {
  return <Fragment key={i}>{i > 0 ? <div>+</div> : null}{m}</Fragment>;
}